import { getLowestPriorPrice } from '@aboutyou/legacy-shared/src/bapi/product/getLowestPriorPrice'
import { SustainabilityIconConditions } from '@aboutyou/legacy-shared/src/bapi/product/getSustainabilityIconConditions'
import {
  ImageConfig,
  ProductImage,
} from '@aboutyou/legacy-shared/src/types/images'
import { BadgeListProps } from '@aboutyou/legacy-shared/src/ui/BadgeList/types'
import { Currency } from '@aboutyou/legacy-shared/src/utils/currency'
import {
  DetailColorId,
  detailColorToHexCode,
  isLightColor,
} from '@aboutyou/legacy-shared/src/utils/detailColor'
import { getCdnProductImageUrl } from '@aboutyou/legacy-shared/src/utils/images'
import {
  checkIsVariant,
  shouldShowFromPrice,
} from 'components/Product/ProductPrices/helpers'
import { SortingChannels } from 'constants/sorting'
import { exists } from 'helpers/exists'
import { ProductModifiers } from 'helpers/products'
import { MouseEvent } from 'react'
import { TranslateFnType } from 'store'
import Product from 'store/entities/Product/Product.store'
import { ImageType } from 'store/features/imageView'
import { LocaleType } from 'types/locale'
import { getProductTileBadgesList } from './getProductTileBadgesList'
import { generateSrcSet, imageHasGreyBackground } from './images'
import { getFormattedEntityPrices } from './prices'

interface IProductTile {
  id: string
  name: string
  productName: string
  href: string
  badgesList: BadgeListProps
  colorsList?: { code: string; isLightColor: boolean }[]
  variantsList?: { value: string }[]
  primaryImageDefaultSource: string
  primaryImageTypeTitle: string
  secondaryImageDefaultSource?: string
  secondaryImageTypeTitle?: string
  primaryImageSrcSet: string
  secondaryImageSrcSet?: string
  sizes?: string
  primaryPadded: boolean
  secondaryPadded: boolean
  formattedBasePrice: string
  formattedSalePrice?: string
  formattedCampaignPrice?: string
  isSponsored?: boolean
  soldout: boolean
  sustainability?: SustainabilityIconConditions
  lowestPriorPrice?: string
  onClick?: (ev: MouseEvent) => void
  onWishlistClick: (ev: MouseEvent) => Promise<void>
  secondaryLabelBasePrice?: string
  secondaryLabelSalePrice?: string
  secondaryLabelCampaignPrice?: string
  isMarketplaceProduct: boolean
  isSecondHandProduct: boolean
  isAggregatedProduct: boolean
}

interface IProductToTileShapeConfig {
  preferredImageType?: ImageType
  isSimilarProduct?: boolean
  inSustainableCategory?: boolean
  currency: Currency
  locale: LocaleType
  selectedVariantId?: string | null
  campaignColor?: string
  likedBrandIds: Set<string>
  basketProductIds: Set<string>
  sortChannel: SortingChannels
  isSaleCampaign: boolean
  showSustainableBadge?: boolean
  features?: Record<string, boolean>
  defaultImageWidth: ImageConfig['width']
  imageWidths: ImageConfig['width'][]
  imageSizes: string[]
  navigate?: (to: string) => React.MouseEventHandler
  toggleWishlistProduct: (product: Product) => Promise<void>
  productModifiers?: ProductModifiers
  excludeSponsoredBadge?: boolean
  isSecondHand?: boolean
  shouldCombineSaleAndCampaignBadges: boolean
  badgeDealTranslation?: (reductionPercentage: string) => string
}

export const productEntityToTileShape = (
  product: Product,
  t: TranslateFnType,
  config: IProductToTileShapeConfig,
): IProductTile => {
  const {
    preferredImageType,
    isSimilarProduct,
    selectedVariantId,
    currency,
    locale,
    campaignColor,
    likedBrandIds,
    basketProductIds,
    sortChannel,
    isSaleCampaign,
    navigate,
    toggleWishlistProduct,
    defaultImageWidth,
    imageWidths,
    imageSizes,
    showSustainableBadge = true,
    productModifiers,
    features,
    shouldCombineSaleAndCampaignBadges,
    badgeDealTranslation,
  } = Object.assign(
    {
      preferredImageType: 'bust',
      isSimilarProduct: false,
    },
    config,
  )

  const preferBust = preferredImageType === 'bust'

  let bustImage
  let bustImageSrcSet
  if (product.defaultImage) {
    const productImage: ProductImage = {
      ...product.defaultImage,
      hash: product.defaultImage.id,
    }
    bustImage = getCdnProductImageUrl(productImage, {
      width: defaultImageWidth,
    })
    bustImageSrcSet = generateSrcSet(productImage, imageWidths)
  }

  let modelImage
  let modelImageSrcSet
  if (product.modelImage) {
    const productImage: ProductImage = {
      ...product.modelImage,
      hash: product.modelImage.id,
    }
    modelImage = getCdnProductImageUrl(productImage, {
      width: defaultImageWidth,
    })
    modelImageSrcSet = generateSrcSet(productImage, imageWidths)
  }

  const variantsList = product.streamSizes
    ? product.streamSizes
        .filter(({ quantity }) => quantity > 0)
        .map(variantEntitySizesToVariantList)
        .filter(exists)
    : undefined

  const detailColorList = []
  if (product.detailColors.length) {
    detailColorList.push(product.detailColors[0].id)
  }
  if (product.siblingColorIds) {
    detailColorList.push(...product.siblingColorIds)
  }
  const colorsList = product.detailColors.length
    ? detailColorList
        .map(color => {
          const code = color
            ? detailColorToHexCode(color as DetailColorId)
            : null
          return code ? { code, isLightColor: isLightColor(color!) } : null
        })
        .filter(exists)
    : undefined

  const selectedVariant = selectedVariantId
    ? product.variants.find(variant => variant.id === selectedVariantId)
    : null

  const prices = getFormattedEntityPrices({
    productPriceRange: product.priceUnitRange,
    productVariants: product.variants,
    selectedVariant: selectedVariant ?? null,
    variantPrice: selectedVariant ? selectedVariant.priceUnit : null,
    config: {
      currencyCode: product.prices.priceRangeRaw.min.currencyCode,
      currency,
      locale,
      t,
      isSaleCampaign,
      features,
    },
  })

  const isVariant = checkIsVariant(selectedVariant ?? null)
  const isAggregatedProduct = shouldShowFromPrice(product, isVariant)

  const isSale = !!(prices && prices.salePrice.formattedPrice)
  const isCampaign = !!(prices && prices.campaignPrice.formattedPrice)
  const productIsSponsored =
    Boolean(product.isSponsored) &&
    (sortChannel === SortingChannels.SponsoredDefault ||
      sortChannel === SortingChannels.SponsoredDefaultAlternative ||
      sortChannel === SortingChannels.SponsoredWithSizeFilter)

  const formattedReduction =
    prices && prices.salePrice.formattedReduction
      ? prices.salePrice.formattedReduction
      : undefined

  const formattedCampaignReduction =
    prices && prices.campaignPrice.formattedReduction
      ? prices.campaignPrice.formattedReduction
      : undefined

  const lowestPriorPrice = getLowestPriorPrice(
    t,
    selectedVariant?.priceRaw?.currencyCode ??
      product.prices.priceRangeRaw.min.currencyCode,
    currency,
    selectedVariant?.lowestPriorPrice ?? product.lowestPriorPrice,
  )

  const displayMode = config.inSustainableCategory ? 'sustainableOnly' : 'all'

  const badgesList = getProductTileBadgesList(product, t, {
    isSale,
    formattedReduction,
    formattedCampaignReduction,
    isCampaign,
    campaignColor,
    likedBrandIds,
    displayMode: config.excludeSponsoredBadge
      ? 'excludeSponsored'
      : displayMode,
    isDisplayedInProductTile: true,
    basketProductIds,
    productIsSponsored,
    isSimilarProduct,
    showSecondHand: false,
    showSustainableBadge,
    productModifiers,
    shouldCombineSaleAndCampaignBadges,
    badgeDealTranslation,
  })

  const bustImageHasGreyBackground = product.defaultImage
    ? imageHasGreyBackground(product.defaultImage)
    : false
  const primaryImageSource = preferBust || !modelImage ? bustImage : modelImage
  const primaryImageSrcSet =
    preferBust || !modelImage ? bustImageSrcSet : modelImageSrcSet
  const primaryImageTypeTitle =
    preferBust || !modelImage ? t('Produktansicht') : t('Modelansicht')
  const primaryIsBust = primaryImageSource === bustImage
  const primaryPadded = primaryIsBust && !bustImageHasGreyBackground
  let secondaryImageSource: string | undefined
  let secondaryImageSrcSet: string | undefined
  let secondaryImageTypeTitle: string | undefined
  let secondaryPadded = false
  if (preferBust) {
    secondaryImageSource = modelImage
    secondaryImageSrcSet = modelImageSrcSet
    secondaryImageTypeTitle = t('Modelansicht')
  }
  // if model image is preferred but not provided bustImage
  // is already used as primaryImageDefaultSource
  else if (!primaryIsBust) {
    secondaryImageSource = bustImage
    secondaryImageSrcSet = bustImageSrcSet
    secondaryImageTypeTitle = t('Produktansicht')
    secondaryPadded = !bustImageHasGreyBackground
  }

  const href = selectedVariantId
    ? `${product.url}?vid=${selectedVariantId}`
    : product.url

  const onClick = navigate ? navigate(href) : undefined

  const onWishlistClick = async (ev: MouseEvent) => {
    ev.preventDefault()
    await toggleWishlistProduct(product)
  }

  return {
    id: product.id,
    name: product.brandDetails.name || '',
    productName: product.name,
    href,
    primaryImageDefaultSource: primaryImageSource!,
    primaryImageTypeTitle,
    secondaryImageDefaultSource: secondaryImageSource,
    secondaryImageTypeTitle,
    primaryImageSrcSet: primaryImageSrcSet!,
    secondaryImageSrcSet,
    sizes: imageSizes.join(', '),
    primaryPadded,
    secondaryPadded,
    secondaryLabelBasePrice:
      features?.enableSecondaryLabelPhase1 ||
      features?.enableSecondaryLabelPhase2
        ? prices?.basePrice.secondaryPriceLabel
        : undefined,
    secondaryLabelSalePrice:
      features?.enableSecondaryLabelPhase1 ||
      features?.enableSecondaryLabelPhase2
        ? prices?.salePrice.secondaryPriceLabel
        : undefined,
    secondaryLabelCampaignPrice:
      features?.enableSecondaryLabelPhase1 ||
      features?.enableSecondaryLabelPhase2
        ? prices?.campaignPrice.secondaryPriceLabel
        : undefined,
    formattedBasePrice: prices ? prices.basePrice.formattedPrice : '',
    formattedSalePrice:
      prices && prices.salePrice.formattedPrice
        ? prices.salePrice.formattedPrice
        : undefined,
    formattedCampaignPrice:
      prices && prices.campaignPrice.formattedPrice
        ? prices.campaignPrice.formattedPrice
        : undefined,
    variantsList,
    colorsList,
    badgesList,
    onClick,
    onWishlistClick,
    isSponsored: productIsSponsored,
    soldout: product.defaultVariant.productIsActive === false,
    sustainability: product.sustainabilityConditions,
    lowestPriorPrice,
    isMarketplaceProduct: Boolean(product.isMarketplaceProduct),
    isSecondHandProduct: Boolean(config.isSecondHand),
    isAggregatedProduct,
  }
}

const variantEntitySizesToVariantList = (size: {
  name: string
}): { value: string } | null => {
  if (size.name) {
    return { value: size.name }
  }

  return null
}
